import * as vp from 'vopay';
import { utils, WorkBook } from 'xlsx';
import moment from 'moment';
import { FileUploadForm } from '../types';

// Type definitions for processed transaction data
export type ProcessedTransaction = {
  [key: string]: string | number | boolean | undefined;
};

/**
 * Get the branded product label for eLinx products
 * @param product - The product code (ePay, eCollect, eFlex)
 * @returns The branded product label with eLinx™ trademark
 */
export const getProductLabel = (product: string): string => {
  if (product === 'ePay') return 'eLinx™ Pay';
  if (product === 'eCollect') return 'eLinx™ Collect';
  if (product === 'eFlex') return 'eLinx™ Flexible Payments';

  return product;
};

const CanadianProvinces = vp.CANADIAN_PROVENCES;
const USProvinces = vp.US_STATES;
const countries = vp.COUNTRIES;

export function processFile(
  workbook: WorkBook,
  fileUploadForm: FileUploadForm,
): {
  [key: string]: string | number | boolean | undefined;
}[] {
  const sheets: string[][] = [];

  workbook.SheetNames.forEach((sheetName) => {
    const csv = utils.sheet_to_csv(workbook.Sheets[sheetName], { FS: ';', blankrows: false });

    if (csv.length) {
      const csvArr = csv.split('\n');
      sheets.push(csvArr);
    }
  });

  const fileData = sheets[0];

  if (fileData) {
    // based on option chhose next
    const header = fileData[0];
    fileData.shift();
    const CleanedFileData = cleanArray(fileData);

    if (CleanedFileData && CleanedFileData.length > 0) {
      if (header.split(';')[0] === 'Loan #') {
        return processDataPresto(CleanedFileData);
      }

      return processXLSFileData(processXLSFileHeader(header), CleanedFileData, fileUploadForm);
    }
  }

  return [];
}

export function parseCSV(
  fileData: string[],
  fileUploadForm: FileUploadForm,
): {
  [key: string]: string | number | boolean | undefined;
}[] {
  // based on service choose option
  const line1 = fileData[0].split(';');

  if (line1[0] === 'Transfer') {
    return processCSVTransfer(fileData);
  }

  if (fileUploadForm.Product === 'VopayInstant') {
    return processCSVVoPayInstant(fileData);
  }

  if (fileUploadForm.Product === 'e-Transfer' && fileUploadForm.Service === 'Debit') {
    return processCSVMoneyRequest(fileData);
  }

  if (fileUploadForm.Product === 'e-Transfer' && fileUploadForm.Service === 'Credit') {
    return processCSVBulkPayout(fileData);
  }

  if (fileUploadForm.Product === 'eFlex') {
    return processCSVEFlex(fileData);
  }

  if (fileUploadForm.Product === 'eCollect') {
    return processCSVElinx(fileData, 'fund');
  }

  if (fileUploadForm.Product === 'ePay') {
    return processCSVElinx(fileData, 'withdraw');
  }

  if (fileUploadForm.Product === 'EFT') {
    return processCSVEFT(fileData);
  }

  if (fileUploadForm.Product === 'OCT') {
    return processCSVPushToCard(fileData);
  }

  if (fileUploadForm.Product === 'Credit Card') {
    return processCSVCreditCard(fileData);
  }

  if (fileUploadForm.Product === 'ACH') {
    return processCSVACH(fileData);
  }

  if (fileUploadForm.Product === 'RTP') {
    return processCSVRTP(fileData);
  }

  if (fileUploadForm.Product === 'Wire') {
    return processCSVWire(fileData);
  }

  if (fileUploadForm.Product === 'Client Account Transfer') {
    return processCSVClientAccountTransfer(fileData);
  }

  return [];
}

function processCSVClientAccountTransfer(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[0]}`.trim() !== 'TransferType' && values.length > 3 && values.length < 13) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.TransferType = `${values[0]}`.trim();
      transaction.DebitorClientAccountID = `${values[1]}`.trim();
      transaction.RecipientClientAccountID = `${values[2]}`.trim();
      transaction.Amount = parseDollar(`${values[3]}`.trim());
      transaction.FundingRail = `${values[4]}`.trim();
      transaction.WithdrawRail = `${values[5]}`.trim();
      transaction.Currency = `${values[6]}`.trim();

      if (values[7]) {
        transaction.ReverseOnFailure = parseBoolean(`${values[7]}`.trim());
      }

      if (values[8]) {
        transaction.ClientReferenceNumber = `${values[8]}`.trim();
      }

      if (values[9]) {
        transaction.Notes = `${values[9]}`.trim();
      }

      if (values[10]) {
        transaction.TransactionLabel = `${values[10]}`.trim();
      }

      if (values[11]) {
        transaction.IdempotencyKey = `${values[11]}`.trim();
      }

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVTransfer(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    const transaction: {
      [key: string]: string | boolean | number | undefined;
    } = {};

    transaction.RecipientName = values[2];
    transaction.Email = values[3];
    transaction.Question = values[6];
    transaction.Answer = values[7];
    transaction.ClientReferenceNumber = values[14];
    transaction.Currency = values[9];
    transaction.Amount = values[10];
    transaction.Language = values[5];
    transactions.push(transaction);
  }

  return transactions;
}

function processCSVBulkPayout(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0 + offset]}`.trim() !== 'Currency' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.RecipientName = `${values[2 + offset]}`.trim();
      transaction.Email = `${values[3 + offset]}`.trim();
      transaction.Question = `${values[4 + offset]}`.trim();
      transaction.Answer = `${values[5 + offset]}`.trim();
      transaction.ClientReferenceNumber = `${values[7 + offset]}`.trim();
      transaction.Currency = `${values[0 + offset]}`.trim();
      transaction.Amount = parseDollar(`${values[1 + offset]}`.trim());
      transaction.Memo = `${values[6 + offset]}`.trim();
      transaction.IdempotencyKey = `${values[8 + offset]}`.trim();

      if (values[9 + offset]) {
        transaction.ClientAccountID = `${values[9 + offset]}`.trim();
      }

      if (values[10 + offset]) {
        transaction.PhoneNumber = `${values[10 + offset]}`.trim();
      }

      if (values[11 + offset]) {
        transaction.Notes = `${values[11 + offset]}`.trim();
      }

      if (values[12 + offset]) {
        transaction.HashSalt = `${values[12 + offset]}`.trim();
      }

      transactions.push(transaction);

      if (offset === 1) {
        transaction.Document = `${values[0]}`.trim();
      }
    }
  }

  return transactions;
}

function processCSVMoneyRequest(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');
    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0 + offset]}`.trim() !== 'Currency' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.RecipientName = `${values[2 + offset]}`.trim();
      transaction.Email = `${values[3 + offset]}`.trim();
      transaction.ClientReferenceNumber = `${values[5 + offset]}`.trim();
      transaction.Currency = `${values[0 + offset]}`.trim();
      transaction.Amount = parseDollar(`${values[1 + offset]}`.trim());
      transaction.MessageForRecipient = `${values[4 + offset]}`.trim();
      transaction.IdempotencyKey = `${values[6 + offset]}`.trim();

      if (values[7 + offset]) {
        transaction.ClientAccountID = `${values[7 + offset]}`.trim();
      }

      if (values[8 + offset]) {
        transaction.PhoneNumber = `${values[8 + offset]}`.trim();
      }

      if (values[9 + offset]) {
        transaction.Notes = `${values[9 + offset]}`.trim();
      }

      transactions.push(transaction);

      if (offset === 1) {
        transaction.Document = `${values[0]}`.trim();
      }
    }
  }

  return transactions;
}

function processCSVEFT(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0 + offset]}`.trim() !== 'Currency' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      const Name = `${values[2 + offset]}`.trim();

      const nameParts = Name.split(' ');

      if (nameParts.length > 2 || nameParts.length < 2 || values[2 + offset]) {
        transaction.CompanyName = Name;
      } else {
        transaction.FirstName = nameParts[0];
        transaction.LastName = nameParts[1];
      }

      transaction.Address1 = `${values[3 + offset]}`.trim();
      transaction.City = `${values[4 + offset]}`.trim();
      transaction.Country = parseCountry(`${values[6 + offset]}`.trim());
      transaction.Province = parseProvince(`${values[5 + offset]}`.trim(), transaction.Country);
      transaction.PostalCode = `${values[7 + offset]}`.trim();
      transaction.ClientReferenceNumber = `${values[8 + offset]}`.trim();
      transaction.FinancialInstitutionNumber = parseNumber(`${values[9 + offset]}`.trim());
      transaction.BranchTransitNumber = parseNumber(`${values[10 + offset]}`.trim());
      transaction.AccountNumber = parseNumber(`${values[11 + offset]}`.trim());
      transaction.Amount = parseDollar(`${values[1 + offset]}`.trim());
      transaction.Currency = `${values[0 + offset]}`.trim();

      if (values[21 + offset]) {
        transaction.ScheduleStartDate = `${values[21 + offset]}`.trim();
      }

      if (values[20 + offset]) {
        transaction.IdempotencyKey = `${values[20 + offset]}`.trim();
      }

      if (values[12 + offset]) {
        transaction.Token = `${values[12 + offset]}`.trim();
      }

      if (values[13 + offset]) {
        transaction.FlinksAccountID = `${values[13 + offset]}`.trim();
      }

      if (values[14 + offset]) {
        transaction.FlinksLoginID = `${values[14 + offset]}`.trim();
      }

      if (values[15 + offset]) {
        transaction.PlaidPublicToken = `${values[15 + offset]}`.trim();
      }

      if (values[16 + offset]) {
        transaction.PlaidAccessToken = `${values[16 + offset]}`.trim();
      }

      if (values[17 + offset]) {
        transaction.PlaidAccountID = `${values[17 + offset]}`.trim();
      }

      if (values[18 + offset]) {
        transaction.PlaidProcessorToken = `${values[18 + offset]}`.trim();
      }

      if (values[19 + offset]) {
        transaction.InveriteRequestGUID = `${values[19 + offset]}`.trim();
      }

      if (values[22 + offset]) {
        transaction.ClientAccountID = `${values[22 + offset]}`.trim();
      }

      if (values[23 + offset]) {
        transaction.TransactionLabel = `${values[23 + offset]}`.trim();
      }

      if (values[24 + offset]) {
        transaction.Notes = `${values[24 + offset]}`.trim();
      }

      if (offset === 1) {
        transaction.Document = `${values[0]}`.trim();
      }

      transactions.push(transaction);
    }
  }

  return transactions;
}

/**
 * CSV Column mapping for eLinx (ePay/eCollect) files
 */
enum ELinxCSVColumns {
  // Client AccountID
  ClientAccountID = 0,
  // Sender Name
  SenderName = 1,
  // Receiver Name
  ReceiverName = 2,
  // Banking Payment Rail
  BankingPaymentRail = 3,
  // Message for Recipient
  MessageForRecipient = 4,
  // Amount
  Amount = 5,
  // Frequency
  Frequency = 6,
  // Name Of Frequency
  NameOfFrequency = 7,
  // Schedule Start Date
  ScheduleStartDate = 8,
  // Schedule End Date
  ScheduleEndDate = 9,
  // Semi Monthly Frequency Type
  SemiMonthlyFrequencyType = 10,
  // Ending After Payments
  EndingAfterPayments = 11,
  // Invoice Number
  InvoiceNumber = 12,
  // DocumentID
  DocumentID = 13,
  // Client Reference Number
  ClientReferenceNumber = 14,
  // Transaction Label
  TransactionLabel = 15,
  // Note
  Note = 16,
  // Send Notification
  SendNotification = 17,
  // Notification Method
  NotificationMethod = 18,
  // Receiver Email Address
  ReceiverEmailAddress = 19,
  // Receiver Phone Number
  ReceiverPhoneNumber = 20,
  // Send Reminder
  SendReminder = 21,
  // Reminder Frequency
  ReminderFrequency = 22,
  // Payment Method Display Options
  PaymentMethodDisplayOptions = 23,
  // Iframe Key
  IframeKey = 24,
}

function processCSVElinx(fileData: string[], paymentType: 'fund' | 'withdraw') {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[ELinxCSVColumns.ClientAccountID]}`.trim() !== 'Client AccountID' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.ClientAccountID = `${values[ELinxCSVColumns.ClientAccountID]}`.trim();

      if (values[ELinxCSVColumns.SenderName]) {
        transaction.SenderName = `${values[ELinxCSVColumns.SenderName]}`.trim();
      }

      transaction.ReceiverName = `${values[ELinxCSVColumns.ReceiverName]}`.trim();

      if (values[ELinxCSVColumns.BankingPaymentRail]) {
        transaction.BankingPaymentRail = `${values[ELinxCSVColumns.BankingPaymentRail]}`.trim();
      }

      if (values[ELinxCSVColumns.MessageForRecipient]) {
        transaction.RecipientMessage = `${values[ELinxCSVColumns.MessageForRecipient]}`.trim();
      }

      if (values[ELinxCSVColumns.Amount]) {
        transaction.Amount = parseDollar(`${values[ELinxCSVColumns.Amount]}`.trim()) || '';
      }

      if (values[ELinxCSVColumns.Frequency]) {
        transaction.Frequency = `${values[ELinxCSVColumns.Frequency]}`.trim();
      }

      if (values[ELinxCSVColumns.NameOfFrequency]) {
        transaction.NameOfFrequency = `${values[ELinxCSVColumns.NameOfFrequency]}`.trim();
      }

      if (values[ELinxCSVColumns.ScheduleStartDate]) {
        transaction.ScheduleStartDate = `${values[ELinxCSVColumns.ScheduleStartDate]}`.trim();
      }

      if (values[ELinxCSVColumns.ScheduleEndDate]) {
        transaction.ScheduleEndDate = `${values[ELinxCSVColumns.ScheduleEndDate]}`.trim();
      }

      if (values[ELinxCSVColumns.SemiMonthlyFrequencyType]) {
        transaction.SemiMonthlyFrequencyType = `${values[ELinxCSVColumns.SemiMonthlyFrequencyType]}`.trim();
      }

      if (values[ELinxCSVColumns.EndingAfterPayments]) {
        transaction.EndingAfterPayments = `${values[ELinxCSVColumns.EndingAfterPayments]}`.trim();
      }

      if (values[ELinxCSVColumns.InvoiceNumber]) {
        transaction.InvoiceNumber = `${values[ELinxCSVColumns.InvoiceNumber]}`.trim();
      }

      if (values[ELinxCSVColumns.DocumentID]) {
        transaction.DocumentID = `${values[ELinxCSVColumns.DocumentID]}`.trim();
      }

      if (values[ELinxCSVColumns.ClientReferenceNumber]) {
        transaction.ClientReferenceNumber = `${values[ELinxCSVColumns.ClientReferenceNumber]}`.trim();
      }

      if (values[ELinxCSVColumns.TransactionLabel]) {
        transaction.TransactionLabel = `${values[ELinxCSVColumns.TransactionLabel]}`.trim();
      }

      if (values[ELinxCSVColumns.Note]) {
        transaction.Note = `${values[ELinxCSVColumns.Note]}`.trim();
      }

      if (values[ELinxCSVColumns.SendNotification]) {
        const sendNotification = `${values[ELinxCSVColumns.SendNotification]}`.trim().toLowerCase();

        transaction.SendNotification =
          sendNotification === 'true' || sendNotification === '1' || sendNotification === 'yes';
      }

      if (values[ELinxCSVColumns.NotificationMethod]) {
        transaction.NotificationMethod = `${values[ELinxCSVColumns.NotificationMethod]}`.trim();
      }

      if (values[ELinxCSVColumns.ReceiverEmailAddress]) {
        transaction.ReceiverEmailAddress = `${values[ELinxCSVColumns.ReceiverEmailAddress]}`.trim();
      }

      if (values[ELinxCSVColumns.ReceiverPhoneNumber]) {
        transaction.ReceiverPhoneNumber = `${vp.NumbersOnly(values[ELinxCSVColumns.ReceiverPhoneNumber])}`.trim();
      }

      if (values[ELinxCSVColumns.SendReminder]) {
        const sendReminder = `${values[ELinxCSVColumns.SendReminder]}`.trim().toLowerCase();
        transaction.SendReminder = sendReminder === 'true' || sendReminder === '1' || sendReminder === 'yes';
      }

      if (values[ELinxCSVColumns.ReminderFrequency]) {
        transaction.ReminderFrequency = `${values[ELinxCSVColumns.ReminderFrequency]}`.trim();
      }

      if (values[ELinxCSVColumns.PaymentMethodDisplayOptions]) {
        transaction.PaymentMethodDisplayOptions = `${values[ELinxCSVColumns.PaymentMethodDisplayOptions]}`.trim();
      }

      if (values[ELinxCSVColumns.IframeKey]) {
        transaction.IframeKey = `${values[ELinxCSVColumns.IframeKey]}`.trim();
      }

      transaction.PaymentType = paymentType;

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVVoPayInstant(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[1]}`.trim() !== 'Currency' && values.length > 3 && values.length < 13) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      const name = `${values[3]}`.trim();
      const nameParts = name.split(' ');

      if (nameParts.length > 2 || nameParts.length < 2) {
        transaction.CompanyName = name;
      } else {
        transaction.FirstName = nameParts[0];
        transaction.LastName = nameParts[1];
      }

      transaction.ClientReferenceNumber = `${values[4]}`.trim();
      transaction.FinancialInstitutionNumber = parseNumber(`${values[5]}`.trim());
      transaction.BranchTransitNumber = parseNumber(`${values[6]}`.trim());
      transaction.AccountNumber = parseNumber(`${values[7]}`.trim());
      transaction.Amount = parseDollar(`${values[2]}`.trim());
      transaction.Currency = `${values[1]}`.trim();

      if (values[8]) {
        transaction.Token = `${values[8]}`.trim();
      }

      if (values[9]) {
        transaction.IdempotencyKey = `${values[9]}`.trim();
      }

      if (values[10]) {
        transaction.ClientAccountID = `${values[10]}`.trim();
      }

      if (values[11]) {
        transaction.Notes = `${values[11]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();
      transactions.push(transaction);
    }
  }

  return transactions;
}

/**
 * CSV Column mapping for eFlex (Flexible Payments) files
 */
enum EFlexCSVColumns {
  // Client AccountID
  ClientAccountID = 0,
  // Sender Name
  SenderName = 1,
  // Receiver Name
  ReceiverName = 2,
  // Banking Payment Rail
  BankingPaymentRail = 3,
  // Message for Recipient
  MessageForRecipient = 4,
  // Amount
  Amount = 5,
  // Frequency
  Frequency = 6,
  // Name Of Frequency
  NameOfFrequency = 7,
  // Schedule Start Date
  ScheduleStartDate = 8,
  // Schedule End Date
  ScheduleEndDate = 9,
  // Semi Monthly Frequency Type
  SemiMonthlyFrequencyType = 10,
  // Ending After Payments
  EndingAfterPayments = 11,
  // Flexible Payment Options
  FlexiblePaymentOptions = 12,
  // Minimum Payment Accepted
  MinimumPaymentAccepted = 13,
  // Remainder Payment Date
  RemainderPaymentDate = 14,
  // Number of Installments
  NumberOfInstallments = 15,
  // Installment Frequency
  InstallmentFrequency = 16,
  // Invoice Number
  InvoiceNumber = 17,
  // DocumentID
  DocumentID = 18,
  // Client Reference Number
  ClientReferenceNumber = 19,
  // Transaction Label
  TransactionLabel = 20,
  // Note
  Note = 21,
  // Send Notification
  SendNotification = 22,
  // Notification Method
  NotificationMethod = 23,
  // Receiver Email Address
  ReceiverEmailAddress = 24,
  // Receiver Phone Number
  ReceiverPhoneNumber = 25,
  // Send Reminder
  SendReminder = 26,
  // Reminder Frequency
  ReminderFrequency = 27,
  // Payment Method Display Options
  PaymentMethodDisplayOptions = 28,
  // Iframe Key
  IframeKey = 29,
}

function processCSVEFlex(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[EFlexCSVColumns.ClientAccountID]}`.trim() !== 'Client AccountID' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.ClientAccountID = `${values[EFlexCSVColumns.ClientAccountID]}`.trim();

      if (values[EFlexCSVColumns.SenderName]) {
        transaction.SenderName = `${values[EFlexCSVColumns.SenderName]}`.trim();
      }

      transaction.ReceiverName = `${values[EFlexCSVColumns.ReceiverName]}`.trim();

      if (values[EFlexCSVColumns.BankingPaymentRail]) {
        transaction.BankingPaymentRail = `${values[EFlexCSVColumns.BankingPaymentRail]}`.trim();
      }

      if (values[EFlexCSVColumns.MessageForRecipient]) {
        transaction.RecipientMessage = `${values[EFlexCSVColumns.MessageForRecipient]}`.trim();
      }

      if (values[EFlexCSVColumns.Amount]) {
        transaction.Amount = parseDollar(`${values[EFlexCSVColumns.Amount]}`.trim()) || '';
      }

      if (values[EFlexCSVColumns.Frequency]) {
        transaction.Frequency = `${values[EFlexCSVColumns.Frequency]}`.trim();
      }

      if (values[EFlexCSVColumns.NameOfFrequency]) {
        transaction.NameOfFrequency = `${values[EFlexCSVColumns.NameOfFrequency]}`.trim();
      }

      if (values[EFlexCSVColumns.ScheduleStartDate]) {
        transaction.ScheduleStartDate = `${values[EFlexCSVColumns.ScheduleStartDate]}`.trim();
      }

      if (values[EFlexCSVColumns.ScheduleEndDate]) {
        transaction.ScheduleEndDate = `${values[EFlexCSVColumns.ScheduleEndDate]}`.trim();
      }

      if (values[EFlexCSVColumns.SemiMonthlyFrequencyType]) {
        transaction.SemiMonthlyFrequencyType = `${values[EFlexCSVColumns.SemiMonthlyFrequencyType]}`.trim();
      }

      if (values[EFlexCSVColumns.EndingAfterPayments]) {
        transaction.EndingAfterPayments = `${values[EFlexCSVColumns.EndingAfterPayments]}`.trim();
      }

      // eFlex-specific fields
      if (values[EFlexCSVColumns.FlexiblePaymentOptions]) {
        transaction.FlexiblePaymentOptions = `${values[EFlexCSVColumns.FlexiblePaymentOptions]}`.trim();
      }

      if (values[EFlexCSVColumns.MinimumPaymentAccepted]) {
        transaction.MinimumPaymentAccepted =
          parseDollar(`${values[EFlexCSVColumns.MinimumPaymentAccepted]}`.trim()) || '';
      }

      if (values[EFlexCSVColumns.RemainderPaymentDate]) {
        transaction.RemainderPaymentDate = `${values[EFlexCSVColumns.RemainderPaymentDate]}`.trim();
      }

      if (values[EFlexCSVColumns.NumberOfInstallments]) {
        transaction.NumberOfInstallments = `${values[EFlexCSVColumns.NumberOfInstallments]}`.trim();
      }

      if (values[EFlexCSVColumns.InstallmentFrequency]) {
        transaction.InstallmentFrequency = `${values[EFlexCSVColumns.InstallmentFrequency]}`.trim();
      }

      if (values[EFlexCSVColumns.InvoiceNumber]) {
        transaction.InvoiceNumber = `${values[EFlexCSVColumns.InvoiceNumber]}`.trim();
      }

      if (values[EFlexCSVColumns.DocumentID]) {
        transaction.DocumentID = `${values[EFlexCSVColumns.DocumentID]}`.trim();
      }

      if (values[EFlexCSVColumns.ClientReferenceNumber]) {
        transaction.ClientReferenceNumber = `${values[EFlexCSVColumns.ClientReferenceNumber]}`.trim();
      }

      if (values[EFlexCSVColumns.TransactionLabel]) {
        transaction.TransactionLabel = `${values[EFlexCSVColumns.TransactionLabel]}`.trim();
      }

      if (values[EFlexCSVColumns.Note]) {
        transaction.Note = `${values[EFlexCSVColumns.Note]}`.trim();
      }

      if (values[EFlexCSVColumns.SendNotification]) {
        const sendNotification = `${values[EFlexCSVColumns.SendNotification]}`.trim().toLowerCase();

        transaction.SendNotification =
          sendNotification === 'true' || sendNotification === '1' || sendNotification === 'yes';
      }

      if (values[EFlexCSVColumns.NotificationMethod]) {
        transaction.NotificationMethod = `${values[EFlexCSVColumns.NotificationMethod]}`.trim();
      }

      if (values[EFlexCSVColumns.ReceiverEmailAddress]) {
        transaction.ReceiverEmailAddress = `${values[EFlexCSVColumns.ReceiverEmailAddress]}`.trim();
      }

      if (values[EFlexCSVColumns.ReceiverPhoneNumber]) {
        transaction.ReceiverPhoneNumber = `${values[EFlexCSVColumns.ReceiverPhoneNumber]}`.trim();
      }

      if (values[EFlexCSVColumns.SendReminder]) {
        const sendReminder = `${values[EFlexCSVColumns.SendReminder]}`.trim().toLowerCase();
        transaction.SendReminder = sendReminder === 'true' || sendReminder === '1' || sendReminder === 'yes';
      }

      if (values[EFlexCSVColumns.ReminderFrequency]) {
        transaction.ReminderFrequency = `${values[EFlexCSVColumns.ReminderFrequency]}`.trim();
      }

      if (values[EFlexCSVColumns.PaymentMethodDisplayOptions]) {
        transaction.PaymentMethodDisplayOptions = `${values[EFlexCSVColumns.PaymentMethodDisplayOptions]}`.trim();
      }

      if (values[EFlexCSVColumns.IframeKey]) {
        transaction.IframeKey = `${values[EFlexCSVColumns.IframeKey]}`.trim();
      }

      transaction.PaymentType = 'fund';

      transactions.push(transaction);
    }
  }

  return transactions;
}

export function processBankingFileStandard005(fileDataStandard: string[]) {
  const Currency = 'CAD';
  const transactions = [];
  const fileData = [...fileDataStandard];

  for (let index = 0; index < fileData.length; index += 1) {
    if (fileData[index].charAt(0) === 'D' || fileData[index].charAt(0) === 'C') {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.InvalidSize = fileData[index].length !== 1464;
      transaction.FinancialInstitutionNumber = fileData[index].substring(44, 47);

      transaction.BranchTransitNumber = fileData[index].substring(47, 52);

      transaction.AccountNumber = fileData[index].substring(52, 64).replace(/\D/g, '');

      transaction.ClientReferenceNumber = fileData[index].substring(174, 193);

      const Name = fileData[index].substring(104, 134).replace(/[^\w\s]/gi, '');

      if (
        Name.split(' ').slice(0, -1).join(' ') !== '' &&
        Name.split(' ').slice(-1).join(' ') !== '' &&
        Name.split(' ').slice(0, -1).join(' ') !== null &&
        Name.split(' ').slice(-1).join(' ') !== null
      ) {
        transaction.FirstName = Name.split(' ').slice(0, -1).join(' ');
        transaction.LastName = Name.split(' ').slice(-1).join(' ');
      } else {
        transaction.CompanyName = Name;
      }

      let amount = fileData[index].substring(27, 37);

      transaction.ScheduleStartDate = Format005Date(fileData[index].substring(37, 43));
      amount = `${amount.substring(0, amount.length - 2)}.${amount.substring(amount.length - 2)}`;
      transaction.Amount = parseFloat(amount);
      transaction.Currency = Currency;
      transaction.Country = 'CA';
      transaction.Address1 = 'n/a';
      transaction.City = 'n/a';
      transaction.PostalCode = 'A1A1A1';
      transaction.Province = 'AB';

      transaction.Notes = fileData[index].substring(229, 251);

      transactions.push(transaction);
      fileData[index] = fileData[index].substr(264);

      while (fileData[index].length >= 100) {
        const subTransactions: {
          [key: string]: string | boolean | number | undefined;
        } = {};

        subTransactions.FinancialInstitutionNumber = fileData[index].substring(20, 23);
        subTransactions.BranchTransitNumber = fileData[index].substring(23, 28);
        subTransactions.AccountNumber = fileData[index].substring(28, 40).replace(/\D/g, '');
        const SubName = fileData[index].substring(80, 110).replace(/\s+$/g, '');

        if (
          SubName.split(' ').slice(0, -1).join(' ') !== '' &&
          SubName.split(' ').slice(-1).join(' ') !== '' &&
          SubName.split(' ').slice(0, -1).join(' ') !== null &&
          SubName.split(' ').slice(-1).join(' ') !== null
        ) {
          subTransactions.FirstName = SubName.split(' ').slice(0, -1).join(' ');
          subTransactions.LastName = SubName.split(' ').slice(-1).join(' ');
        } else {
          subTransactions.CompanyName = SubName;
        }

        let subAmount = fileData[index].substring(3, 13);
        subAmount = `${subAmount.substring(0, subAmount.length - 2)}.${subAmount.substring(subAmount.length - 2)}`;

        subTransactions.ScheduleStartDate = Format005Date(fileData[index].substring(13, 19));
        subTransactions.Amount = parseFloat(subAmount);
        subTransactions.Currency = Currency;
        subTransactions.Country = 'CA';
        subTransactions.Address1 = 'n/a';
        subTransactions.City = 'n/a';
        subTransactions.PostalCode = 'A1A1A1';
        subTransactions.Province = 'AB';

        subTransactions.Notes = fileData[index].substring(205, 227);

        if (!Number.isNaN(subTransactions.Amount)) {
          transactions.push(subTransactions);
        }

        fileData[index] = fileData[index].substr(240);
      }
    }
  }

  return transactions;
}

export function processBankingFileBMO80(fileData: string[]) {
  const Currency = 'CAD';
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    if (fileData[index].charAt(0) === 'D' || fileData[index].charAt(0) === 'C') {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.InvalidSize = !!(fileData[index].length < 61 || fileData[index].length > 80);
      transaction.FinancialInstitutionNumber = fileData[index].substring(12, 15);
      transaction.BranchTransitNumber = fileData[index].substring(15, 20);
      transaction.AccountNumber = fileData[index].substring(20, 32).replace(/\s+$/g, '');
      const Name = fileData[index].substring(32, 60).replace(/\s+$/g, '');

      if (
        Name.split(' ').slice(0, -1).join(' ') !== '' &&
        Name.split(' ').slice(-1).join(' ') !== '' &&
        Name.split(' ').slice(0, -1).join(' ') !== null &&
        Name.split(' ').slice(-1).join(' ') !== null
      ) {
        transaction.FirstName = Name.split(' ').slice(0, -1).join(' ');
        transaction.LastName = Name.split(' ').slice(-1).join(' ');
      } else {
        transaction.CompanyName = Name;
      }

      let amount = fileData[index].substring(1, 11);
      amount = `${amount.substring(0, amount.length - 2)}.${amount.substring(amount.length - 2)}`;
      transaction.Amount = parseFloat(amount);
      transaction.Currency = Currency;
      transaction.Country = 'CA';
      transaction.Address1 = 'n/a';
      transaction.City = 'n/a';
      transaction.PostalCode = 'n/a';
      transaction.Province = 'AB';
      transaction.ClientReferenceNumber = fileData[index].substring(61, 80).replace(/\s+$/g, '');
      transactions.push(transaction);
    }
  }

  return transactions;
}

export function processMoneyWay(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const fileLineValues = fileData[index].split('\t');

    if (fileLineValues[0] !== 'Currency' && fileLineValues[0] !== '') {
      const transaction: {
        [key: string]: string | undefined;
      } = {};

      const name = parseName(fileLineValues[2]);
      const nameParts = name.split(' ');

      if (nameParts.length > 2 || nameParts.length < 2) {
        transaction.CompanyName = name;
      } else {
        transaction.FirstName = nameParts[0];
        transaction.LastName = nameParts[1];
      }

      transaction.Currency = fileLineValues[0];
      transaction.Address1 = fileLineValues[3];
      transaction.City = fileLineValues[4];
      transaction.Country = parseCountry(fileLineValues[6]);
      transaction.Province = parseProvince(fileLineValues[5], transaction.Country);

      transaction.PostalCode = fileLineValues[7];
      transaction.ClientReferenceNumber = fileLineValues[8];
      transaction.FinancialInstitutionNumber = parseNumber(fileLineValues[9]);
      transaction.BranchTransitNumber = parseNumber(fileLineValues[10]);
      transaction.AccountNumber = parseNumber(fileLineValues[11]);
      transaction.Amount = parseDollar(fileLineValues[1]);
      transaction.Currency = fileLineValues[0];
      transactions.push(transaction);
    }
  }

  return transactions;
}

function processDataPresto(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const fileLineValues = fileData[index].split(';');

    const transaction: {
      [key: string]: string | undefined;
    } = {};

    if (fileLineValues[0] !== '') {
      transaction.ClientReferenceNumber = fileLineValues[0];
      transaction.FirstName = parseName(fileLineValues[3]);
      transaction.LastName = parseName(fileLineValues[4]);
      transaction.Address1 = fileLineValues[5];
      transaction.City = fileLineValues[6];
      transaction.Province = parseProvince(fileLineValues[7]);

      if (fileLineValues[8] === '') {
        transaction.Country = 'CA';
      } else {
        transaction.Country = parseCountry(fileLineValues[8]);
      }

      transaction.PostalCode = parsePostalCode(fileLineValues[9]);
      transaction.AccountNumber = parseNumber(fileLineValues[10]);
      transaction.FinancialInstitutionNumber = parseNumber(fileLineValues[11]);
      transaction.BranchTransitNumber = parseNumber(fileLineValues[12]);
      transaction.Amount = parseDollar(fileLineValues[13]);
      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVPushToCard(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[0]}`.trim() !== 'Service' && values.length > 3 && values.length < 8) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      let Name = '';

      if (values[2]) {
        Name = `${values[2]}`.trim();
        transaction.ClientAccountID = Name;
        transaction.CompanyName = Name;
      } else {
        transaction.FirstName = '';
        transaction.LastName = '';
      }

      transaction.CardToken = `${values[3]}`.trim();
      transaction.Amount = parseDollar(`${values[4]}`.trim());
      transaction.Currency = `${values[1]}`.trim();
      transaction.Country = parseCountry(values[5]);

      if (values[6]) {
        transaction.IdempotencyKey = `${values[6]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVCreditCard(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    if (`${values[0]}`.trim() !== 'Service' && values.length > 3 && values.length < 8) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      let Name = '';

      if (values[3]) {
        Name = `${values[3]}`.trim();
        transaction.ClientAccountID = Name;
        transaction.CompanyName = Name;
      } else {
        transaction.FirstName = '';
        transaction.LastName = '';
      }

      transaction.CreditCardToken = `${values[4]}`.trim();
      transaction.Amount = parseDollar(`${values[2]}`.trim());
      transaction.Currency = `${values[1]}`.trim();
      transaction.Country = parseCountry(values[5]);

      if (values[6]) {
        transaction.IdempotencyKey = `${values[6]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVACH(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0]}`.trim() !== 'Service' && values.length > 3) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      const name = `${values[2 + offset]}`.trim();

      const nameParts = name.split(' ');

      if (nameParts.length > 2 || nameParts.length < 2) {
        transaction.CompanyName = name;
      } else {
        transaction.FirstName = nameParts[0];
        transaction.LastName = nameParts[1];
      }

      transaction.Amount = parseDollar(`${values[0 + offset]}`.trim());
      transaction.Address1 = `${values[3 + offset]}`.trim();
      transaction.City = `${values[5 + offset]}`.trim();
      transaction.Country = parseCountry(values[7 + offset]);
      transaction.State = parseProvince(`${values[6 + offset]}`.trim(), transaction.Country);
      transaction.ZipCode = `${values[8 + offset]}`.trim();
      transaction.ABARoutingNumber = `${values[9 + offset]}`.trim();
      transaction.AccountNumber = `${values[10 + offset]}`.trim();
      transaction.PhoneNumber = `${values[17 + offset]}`.trim();
      transaction.IdentificationNumber = `${values[11 + offset]}`.trim();

      if (values[1 + offset]) {
        transaction.EmailAddress = `${values[1 + offset]}`.trim();
      }

      if (values[4 + offset]) {
        transaction.Address2 = `${values[4 + offset]}`.trim();
      }

      if (values[12 + offset]) {
        transaction.ClientAccountID = `${values[12 + offset]}`.trim();
      }

      if (values[13 + offset]) {
        transaction.IPAddress = `${values[13 + offset]}`.trim();
      }

      if (values[14 + offset]) {
        transaction.Token = `${values[14 + offset]}`.trim();
      }

      if (values[15 + offset]) {
        transaction.PlaidProcessorToken = `${values[15 + offset]}`.trim();
      }

      if (values[16 + offset]) {
        transaction.Notes = `${values[16 + offset]}`.trim();
      }

      if (values[18 + offset]) {
        transaction.IdempotencyKey = `${values[18 + offset]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVRTP(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0]}`.trim() !== 'Service' && values.length > 3 && values.length < 11) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      transaction.Amount = `${values[0 + offset]}`.trim();
      transaction.RecipientName = `${values[2 + offset]}`.trim();
      transaction.AccountNumber = `${values[3 + offset]}`.trim();
      transaction.ABARoutingNumber = `${values[4 + offset]}`.trim();

      if (values[1 + offset]) {
        transaction.ClientAccountID = `${values[1 + offset]}`.trim();
      }

      if (values[5 + offset]) {
        transaction.Token = `${values[5 + offset]}`.trim();
      }

      if (values[6 + offset]) {
        transaction.ClientReferenceNumber = `${values[6 + offset]}`.trim();
      }

      if (values[7 + offset]) {
        transaction.Notes = `${values[7 + offset]}`.trim();
      }

      if (values[8 + offset]) {
        transaction.IdempotencyKey = `${values[8 + offset]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();

      transactions.push(transaction);
    }
  }

  return transactions;
}

function processCSVWire(fileData: string[]) {
  const transactions = [];

  for (let index = 0; index < fileData.length; index += 1) {
    const values = fileData[index].split(';');

    let offset = 0;

    if (
      values[0].toLowerCase() === 'debit' ||
      values[0].toLowerCase() === 'credit' ||
      values[0].toLowerCase() === 'service' ||
      values.length === 9 ||
      values.length === 13 ||
      values.length === 7
    ) {
      offset = 1;
    }

    if (`${values[0]}`.trim() !== 'Service' && values.length > 3 && values.length < 19) {
      const transaction: {
        [key: string]: string | boolean | number | undefined;
      } = {};

      const name = `${values[2 + offset]}`.trim();

      const nameParts = name.split(' ');

      if (nameParts.length > 2 || nameParts.length < 2) {
        transaction.CompanyName = name;
      } else {
        transaction.FirstName = nameParts[0];
        transaction.LastName = nameParts[1];
      }

      transaction.EmailAddress = `${values[1 + offset]}`.trim();
      transaction.PhoneNumber = `${values[3 + offset]}`.trim();
      transaction.Address1 = `${values[4 + offset]}`.trim();
      transaction.City = `${values[6 + offset]}`.trim();
      transaction.State = `${values[7 + offset]}`.trim();
      transaction.Country = `${values[8 + offset]}`.trim();
      transaction.ZipCode = `${values[9 + offset]}`.trim();
      transaction.ABARoutingNumber = `${values[10 + offset]}`.trim();
      transaction.AccountNumber = `${values[11 + offset]}`.trim();
      transaction.Amount = `${values[0 + offset]}`.trim();

      if (values[12 + offset]) {
        transaction.ClientAccountID = `${values[12 + offset]}`.trim();
      }

      if (values[13 + offset]) {
        transaction.IPAddress = `${values[13 + offset]}`.trim();
      }

      if (values[14 + offset]) {
        transaction.Notes = `${values[14 + offset]}`.trim();
      }

      if (values[15 + offset]) {
        transaction.IdempotencyKey = `${values[15 + offset]}`.trim();
      }

      if (values[5 + offset]) {
        transaction.Address2 = `${values[5 + offset]}`.trim();
      }

      transaction.Document = `${values[0]}`.trim();

      transactions.push(transaction);
    }
  }

  return transactions;
}

function Format005Date(value: string) {
  const str = value.substring(1);
  const date = moment(str, 'YYDDD').format('YYYY-MM-DD');

  return date;
}

function getKeyByValue(object: { [key: string]: string }, value: string): string {
  return Object.keys(object).find((key) => object[key].toLowerCase() === value.toLowerCase()) || '';
}

function getIsoCountry(name: string) {
  const ctr = Object.values(countries)
    .filter((f) => f.Country.toUpperCase() === name.toUpperCase())
    .map((c) => c.Abbreviation);

  return ctr[0];
}

function parsePostalCode(value: string): string {
  if (value != null) {
    return value.replace(/[^a-zA-Z0-9]+/g, '');
  }

  return value;
}

function tofloat(numberString: string): number {
  return parseFloat(numberString.replace(/[^0-9.-]+/g, ''));
}

function parseName(value: string): string {
  if (value) {
    return value.replace(/[^a-zA-Z0-9=$\s.*-]+/g, '');
  }

  return value;
}

export function parseCountry(value: string): string {
  if (value) {
    if (value.length > 2) {
      if (value.toLowerCase() === 'canada') {
        return 'CA';
      }

      if (getIsoCountry(value)) {
        return getIsoCountry(value);
      }

      return value.toUpperCase();
    }

    return value.toUpperCase();
  }

  return (value || '').toUpperCase();
}

function parseNumber(value: string): string {
  if (value) {
    return value.replace(/[\D]+/g, '');
  }

  return value;
}

function parseDollar(value: string): string {
  if (value) {
    return value.replace(/[^\d.]+/g, '');
  }

  return value;
}

function parseBoolean(value: string): boolean {
  if (value) {
    return value.toLowerCase() === 'true';
  }

  return false;
}

export function parseProvince(value: string, country = 'CA'): string | undefined {
  let isDefined = false;

  if (value) {
    if (value.length > 2) {
      if (country === 'CA') {
        return getKeyByValue(CanadianProvinces, value);
      }

      if (country === 'US') {
        return getKeyByValue(USProvinces, value);
      }

      return undefined;
    }

    if (value.toUpperCase() === 'PQ') {
      return 'QC';
    }

    if (value.toUpperCase() === 'QB') {
      return 'QC';
    }

    if (value.toUpperCase() === 'NF') {
      return 'NL';
    }

    if (country === 'CA') {
      isDefined = Object.prototype.hasOwnProperty.call(CanadianProvinces, value.toUpperCase());
    }

    if (country === 'US') {
      isDefined = Object.prototype.hasOwnProperty.call(USProvinces, value.toUpperCase());
    }

    if (isDefined) {
      return value.toUpperCase();
    }

    return undefined;
  }

  return (value || '').toUpperCase();
}

function processXLSFileData(
  headerPos: {
    [key: string]: number | null;
  },
  data: string[],
  fileUploadForm: FileUploadForm,
): {
  [key: string]: string | number;
}[] {
  const arrayObjects = [];

  for (let index = 0; index < data.length; index += 1) {
    if (data[index].split(';')[1] !== 'Total') {
      if (fileUploadForm.Product === 'e-Transfer') {
        arrayObjects.push(getEmailObject(headerPos, data[index].split(';')));
      } else {
        arrayObjects.push(getBankingObject(headerPos, data[index].split(';')));
      }
    }
  }

  return arrayObjects;
}

function getEmailObject(
  headerPos: {
    [key: string]: number | null;
  },
  data: string[],
) {
  const emailObject: { [key: string]: number | string } = {};

  if (headerPos.FirstName != null && headerPos.LastName != null) {
    emailObject.FirstName = parseName(data[headerPos.FirstName]);
    emailObject.LastName = parseName(data[headerPos.LastName]);
    emailObject.RecipientName = `${parseName(data[headerPos.FirstName])} ${parseName(data[headerPos.LastName])}`;
  }

  if (headerPos.FullName != null) {
    const Name = parseName(data[headerPos.FullName]);

    if (
      Name.split(' ').slice(0, -1).join(' ') !== '' &&
      Name.split(' ').slice(-1).join(' ') !== '' &&
      Name.split(' ').slice(0, -1).join(' ') != null &&
      Name.split(' ').slice(-1).join(' ') != null
    ) {
      emailObject.FirstName = Name.split(' ').slice(0, -1).join(' ');
      emailObject.LastName = Name.split(' ').slice(-1).join(' ');
    } else {
      emailObject.CompanyName = Name;
    }

    emailObject.RecipientName = parseName(data[headerPos.FullName]);
  }

  if (headerPos.BusinessName != null) {
    emailObject.BusinessName = parseName(data[headerPos.BusinessName]);
    emailObject.RecipientName = parseName(data[headerPos.BusinessName]);
  }

  if (headerPos.RecipientName != null) {
    emailObject.RecipientName = parseName(data[headerPos.RecipientName]);
  }

  if (headerPos.SenderName != null) {
    emailObject.SenderName = parseName(data[headerPos.SenderName]);
  }

  if (headerPos.PendingDue != null) {
    emailObject.Amount = tofloat(data[headerPos.PendingDue]);
  }

  if (headerPos.Amount != null) {
    emailObject.Amount = tofloat(data[headerPos.Amount]);
  }

  if (headerPos.Notes != null) {
    emailObject.Notes = data[headerPos.Notes];
  }

  if (headerPos.Address != null) {
    emailObject.Address1 = data[headerPos.Address].replace(/^-+/, '');
  }

  if (headerPos.Address1 != null) {
    emailObject.Address1 = data[headerPos.Address1].replace(/^-+/, '');
  }

  if (headerPos.Address2 != null) {
    emailObject.Address2 = data[headerPos.Address2].replace(/^-+/, '');
  }

  if (headerPos.Country != null) {
    emailObject.Country = data[headerPos.Country];
  }

  if (headerPos.City != null) {
    emailObject.City = data[headerPos.City];
  }

  if (headerPos.Province != null) {
    emailObject.Province = data[headerPos.Province];
  }

  if (headerPos.Currency != null) {
    emailObject.Currency = data[headerPos.Currency];
  }

  if (headerPos.Email != null) {
    emailObject.Email = data[headerPos.Email].replace(/^-+/, '').replace(/^ +/, '');
    emailObject.EmailAddress = data[headerPos.Email].replace(/^-+/, '').replace(/^ +/, '');
    emailObject.ReceiverEmailAddress = data[headerPos.Email].replace(/^-+/, '').replace(/^ +/, '');
  }

  if (headerPos.PhoneNumber != null) {
    emailObject.PhoneNumber = data[headerPos.PhoneNumber].replace(/\s+$/g, '');
  }

  if (headerPos.Message != null) {
    emailObject.MessageForRecipient = data[headerPos.Message];
  }

  if (headerPos.PostalCode != null) {
    emailObject.PostalCode = data[headerPos.PostalCode];
  }

  if (headerPos.Question != null) {
    emailObject.Question = data[headerPos.Question];
  }

  if (headerPos.Answer != null) {
    emailObject.Answer = data[headerPos.Answer];
  }

  if (headerPos.Salt != null) {
    emailObject.HashSalt = data[headerPos.Salt];
  }

  if (headerPos.ClientReferenceNumber != null) {
    emailObject.ClientReferenceNumber = data[headerPos.ClientReferenceNumber];
  }

  if (emailObject.Currency === null || emailObject.Currency === undefined) {
    emailObject.Currency = 'CAD';
  }

  if (headerPos.Language != null) {
    emailObject.Language = data[headerPos.Language];
  }

  return emailObject;
}

function getBankingObject(
  headerPos: {
    [key: string]: number | null;
  },
  data: string[],
) {
  const bankingObject: { [key: string]: number | string } = {};

  if (headerPos.FirstName != null && headerPos.LastName != null) {
    bankingObject.FirstName = parseName(data[headerPos.FirstName]);
    bankingObject.LastName = parseName(data[headerPos.LastName]);
    bankingObject.RecipientName = `${parseName(data[headerPos.FirstName])} ${parseName(data[headerPos.LastName])}`;
  }

  if (headerPos.FullName != null) {
    const Name = parseName(data[headerPos.FullName]);

    if (
      Name.split(' ').slice(0, -1).join(' ') !== '' &&
      Name.split(' ').slice(-1).join(' ') !== '' &&
      Name.split(' ').slice(0, -1).join(' ') != null &&
      Name.split(' ').slice(-1).join(' ') != null
    ) {
      bankingObject.FirstName = Name.split(' ').slice(0, -1).join(' ');
      bankingObject.LastName = Name.split(' ').slice(-1).join(' ');
    } else {
      bankingObject.CompanyName = Name;
    }
  }

  if (headerPos.BusinessName != null) {
    bankingObject.CompanyName = parseName(data[headerPos.BusinessName]);
    bankingObject.RecipientName = parseName(data[headerPos.BusinessName]);
  }

  if (headerPos.SenderName != null) {
    bankingObject.SenderName = parseName(data[headerPos.SenderName]);
  }

  if (headerPos.PendingDue != null) {
    bankingObject.Amount = tofloat(data[headerPos.PendingDue]);
  }

  if (headerPos.Amount != null) {
    bankingObject.Amount = tofloat(data[headerPos.Amount]);
  }

  if (headerPos.Notes != null) {
    bankingObject.Notes = data[headerPos.Notes];
  }

  if (headerPos.Transit != null) {
    bankingObject.BranchTransitNumber = parseNumber(data[headerPos.Transit]);
  }

  if (headerPos.InstitutionNumber != null) {
    bankingObject.FinancialInstitutionNumber = parseNumber(data[headerPos.InstitutionNumber]);
  }

  if (headerPos.AccountNumber != null) {
    bankingObject.AccountNumber = parseNumber(data[headerPos.AccountNumber]);
  }

  if (headerPos.IBAN != null) {
    bankingObject.Iban = data[headerPos.IBAN];
  }

  if (headerPos.ABA != null) {
    bankingObject.Aba = data[headerPos.ABA];
  }

  if (headerPos.SortCode != null) {
    bankingObject.SortCode = data[headerPos.SortCode];
  }

  if (headerPos.Address != null) {
    bankingObject.Address1 = data[headerPos.Address].replace(/^-+/, '');
  }

  if (headerPos.Address1 != null) {
    bankingObject.Address1 = data[headerPos.Address1].replace(/^-+/, '');
  }

  if (headerPos.Address2 != null) {
    bankingObject.Address2 = data[headerPos.Address2].replace(/^-+/, '');
  }

  if (headerPos.Country != null) {
    bankingObject.Country = data[headerPos.Country];
  }

  if (headerPos.City != null) {
    bankingObject.City = data[headerPos.City];
  }

  if (headerPos.Province != null) {
    bankingObject.Province = data[headerPos.Province];
  }

  if (headerPos.Currency != null) {
    bankingObject.Currency = data[headerPos.Currency];
  }

  if (headerPos.Email != null) {
    bankingObject.Email = data[headerPos.Email].replace(/^-+/, '').replace(/^ +/, '');
    bankingObject.EmailAddress = data[headerPos.Email].replace(/^-+/, '').replace(/^ +/, '');
  }

  if (headerPos.Message != null) {
    bankingObject.MessageForRecipient = data[headerPos.Message];
  }

  if (headerPos.PostalCode != null) {
    bankingObject.PostalCode = data[headerPos.PostalCode];
  }

  if (headerPos.Question != null) {
    bankingObject.Question = data[headerPos.Question];
  }

  if (headerPos.Answer != null) {
    bankingObject.Answer = data[headerPos.Answer];
  }

  if (headerPos.Salt != null) {
    bankingObject.HashSalt = data[headerPos.Salt];
  }

  if (headerPos.ScheduleStartDate !== null) {
    bankingObject.ScheduleStartDate = moment(data[headerPos.ScheduleStartDate]).format('YYYY-MM-DD');
  }

  if (headerPos.DOB != null) {
    bankingObject.DOB = data[headerPos.DOB];
  }

  if (headerPos.PhoneNumber != null) {
    bankingObject.PhoneNumber = data[headerPos.PhoneNumber].replace(/\s+$/g, '');
  }

  if (headerPos.ClientReferenceNumber != null) {
    bankingObject.ClientReferenceNumber = data[headerPos.ClientReferenceNumber];
  }

  if (bankingObject.Currency === null) {
    bankingObject.Currency = 'CAD';
  }

  return bankingObject;
}

function cleanArray(results: string[]) {
  const results2 = [];

  for (let index = 0; index < results.length; index += 1) {
    const temp = results[index].replace(/\s|_/g, '').replace(/,|_/g, '');

    if (temp !== '' && temp !== null) {
      results2.push(results[index]);
    }
  }

  return results2;
}

function processXLSFileHeader(headerLine: string): {
  [key: string]: number | null;
} {
  const headerItems = headerLine.split(';');

  const headers: { [key: string]: number | null } = {
    FirstName: null,
    LastName: null,
    BusinessName: null,
    SenderName: null,
    FullName: null,
    Amount: null,
    Penalty: null,
    AmortizationDue: null,
    PendingDue: null,
    LoanNumber: null,
    Address: null,
    Country: null,
    City: null,
    Province: null,
    Address1: null,
    Address2: null,
    PhoneNumber: null,
    DOB: null,
    ClientReferenceNumber: null,
    Currency: null,
    Email: null,
    PostalCode: null,
    Transit: null,
    IBAN: null,
    ABA: null,
    SortCode: null,
    InstitutionNumber: null,
    AccountNumber: null,
    Question: null,
    Answer: null,
    Message: null,
    Notes: null,
    DueDate: null,
    PastDue: null,
    Salt: null,
    Language: null,
    ScheduleStartDate: null,
  };

  headerItems.forEach((item, index) => {
    const header = String(item.toLowerCase().replace(/\s|_/g, ''));

    if (header.includes('firstname') || header.includes('first_name')) {
      headers.FirstName = index;
    } else if (header.toLowerCase().includes('lastname') || header.toLowerCase().includes('last_name')) {
      headers.LastName = index;
    } else if (
      header.includes('fullname') ||
      header.includes('full_name') ||
      header.includes('csvname') ||
      header.includes('recipientname')
    ) {
      headers.FullName = index;
    } else if (header.includes('sendname') || header.includes('sendername')) {
      headers.SenderName = index;
    } else if (header.includes('name')) {
      headers.BusinessName = index;
    } else if (header.includes('amortization')) {
      headers.AmortizationDue = index;
    } else if (header.includes('pending')) {
      headers.PendingDue = index;
    } else if (header.includes('penalty')) {
      headers.Penalty = index;
    } else if (header.includes('duedate')) {
      headers.DueDate = index;
    } else if (header.includes('pastdue')) {
      headers.PastDue = index;
    } else if (
      header.includes('amount') ||
      header.includes('due') ||
      header.includes('collect') ||
      header.includes('payment') ||
      header.includes('eftamount') ||
      header.includes('amount_$')
    ) {
      headers.Amount = index;
    } else if (header.includes('address') && header.includes('1')) {
      headers.Address1 = index;
    } else if (header.includes('address') && header.includes('2')) {
      headers.Address2 = index;
    } else if (header.includes('address')) {
      headers.Address = index;
    } else if (header.includes('country')) {
      headers.Country = index;
    } else if (header.includes('city')) {
      headers.City = index;
    } else if (header.includes('postalcode') || header.includes('postal_code')) {
      headers.PostalCode = index;
    } else if (header.includes('province') || header.includes('state')) {
      headers.Province = index;
    } else if (
      header.includes('phone') ||
      header.includes('tel') ||
      header.includes('mobile') ||
      header.includes('cell')
    ) {
      headers.PhoneNumber = index;
    } else if (
      header.includes('schedulestartdate') ||
      header.includes('scheduledate') ||
      header.includes('processingdate')
    ) {
      headers.ScheduleStartDate = index;
    } else if (header.includes('date') || header.includes('birth') || header.includes('dob')) {
      headers.DOB = index;
    } else if (header.includes('currency') || header.includes('iso') || header.includes('currencies')) {
      headers.Currency = index;
    } else if (header.includes('email')) {
      headers.Email = index;
    } else if (header.includes('transit') || header.includes('branch') || header.includes('transit#')) {
      headers.Transit = index;
    } else if (header.includes('iban')) {
      headers.IBAN = index;
    } else if (header.includes('aba')) {
      headers.ABA = index;
    } else if (header.includes('sort')) {
      headers.SortCode = index;
    } else if (header.includes('institution') || header.includes('bank#') || header.includes('bankid')) {
      headers.InstitutionNumber = index;
    } else if (header.includes('account') || header.includes('account#') || header.includes('BANK_ACCOUNT')) {
      headers.AccountNumber = index;
    } else if (header.includes('message') || header.includes('memo')) {
      headers.Message = index;
    } else if (header.includes('question') || header.includes('yourclientnumber')) {
      headers.Question = index;
    } else if (header.includes('answer') || header.includes('$base64')) {
      headers.Answer = index;
    } else if (header.includes('note') || header.includes('description')) {
      headers.Notes = index;
    } else if (header.includes('salt')) {
      headers.Salt = index;
    } else if (
      (header.includes('loan') ||
        header.includes('clientnumber') ||
        header.includes('reference') ||
        header.includes('clientrefnumber')) &&
      !header.includes('loan(a,b,c)')
    ) {
      headers.LoanNumber = index;
      headers.ClientReferenceNumber = index;
    } else if (header.includes('language') || header === 'en' || header === 'fr') {
      headers.Language = index;
    }
  });

  return headers;
}
